/**
 * Status island and the embedding/search-index badge built on it
 * (sl: badge-uniformity)
 *
 * SOLID: S — single source of truth for the island's look + interaction.
 * DRY: image blocks, attachment blocks and the PDF viewer (search index and
 * signed copy) render the SAME island from here, so style and click behaviour
 * can no longer drift apart between block types or between Lit and React.
 *
 * `renderStatusIsland` is the shell: a tone, an icon, a label and optional
 * details. `renderEmbeddingBadge` maps the index states onto it. Idle stays
 * compact; hover expands the same island vertically and reveals details and
 * actions. Click keeps that expanded state pinned for users who need more time.
 *
 * `disabled` (workspace embedding policy is off) is a first-class state: the
 * badge stays visible, communicates that the content is not searchable, and the
 * popover tells the user why and what to do (no self-service re-index).
 */
import { css, html, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { themedScrollbarStyle } from '../styles/themed-scrollbar-style';
import { unsafeCSSVarV2 } from '../theme/css-variables';
import {
  getEmbeddingStatusLabel,
  type IEmbeddingStatusService,
} from './embedding-status-service';

export type EmbeddingBadgeStatus =
  | 'searchable'
  | 'paused'
  | 'not-indexed'
  | 'disabled'
  | 'loading'
  | 'failed'
  | 'unavailable';

export interface EmbeddingBadgeDetailRow {
  label: string;
  value: string;
}

export interface EmbeddingBadgeAction {
  label: string;
  onClick: (event: MouseEvent) => void;
  kind?: 'primary' | 'secondary';
}

/** How an island reads at a glance; decides its colour. */
export type StatusIslandTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'neutral';

export interface StatusIslandViewModel {
  tone: StatusIslandTone;
  icon: TemplateResult;
  /** Tooltip, and the text next to the icon once it is revealed. */
  label: string;
  /** Keeps the label next to the icon while collapsed: a pill, not a circle. */
  labelVisible?: boolean;
  /** The consumer's own state name, set as a class on the shell. */
  stateClass?: string;
  /** Busy islands show progress only: no details, no pointer. */
  busy?: boolean;
  /** Whether the details are pinned open. */
  expanded: boolean;
  /** Details heading. */
  title?: string;
  rows?: EmbeddingBadgeDetailRow[];
  /** Details text below the rows: explains the state or how to change it. */
  body?: string;
  actions?: EmbeddingBadgeAction[];
  onClick: (event: MouseEvent) => void;
  /** Controller-owned hover intent state, used when rendered in a portal. */
  hoverIntent?: boolean;
  isPointerPresent?: () => boolean;
  onHoverIntentChange?: (active: boolean) => void;
  onPointerPresenceChange?: (active: boolean) => void;
  reviewOpen?: boolean;
  reviewPanel?: TemplateResult;
}

export interface EmbeddingBadgeViewModel {
  status: EmbeddingBadgeStatus;
  /** Whether the details popover is open. */
  expanded: boolean;
  /** Batch progress shown while loading (e.g. 23/100). */
  progress?: { indexed: number; total: number } | null;
  /** Short label revealed on hover and used as the badge tooltip. */
  hoverLabel: string;
  /** Details popover heading. */
  detailTitle: string;
  /** Details popover body — explains the state / how to change it. */
  detailBody?: string;
  /**
   * Structured detail rows (label/value pairs) shown instead of a plain body.
   * Used by consumers with multiple status axes (e.g. PDF text layer + OCR).
   */
  detailRows?: EmbeddingBadgeDetailRow[];
  /** Primary action label (re-index / index / retry). Omitted when not actionable. */
  actionLabel?: string;
  onBadgeClick: (event: MouseEvent) => void;
  onAction?: (event: MouseEvent) => void;
  actions?: EmbeddingBadgeAction[];
  /** Controller-owned hover intent state, used when a badge is rendered in a portal. */
  hoverIntent?: boolean;
  isPointerPresent?: () => boolean;
  onHoverIntentChange?: (active: boolean) => void;
  onPointerPresenceChange?: (active: boolean) => void;
  reviewOpen?: boolean;
  reviewPanel?: TemplateResult;
}

const stopPropagation = (event: MouseEvent) => event.stopPropagation();
const HOVER_INTENT_DELAY_MS = 90;
const hoverIntentTimers = new WeakMap<
  HTMLElement,
  ReturnType<typeof setTimeout>
>();

function clearHoverIntentTimer(badge: HTMLElement) {
  const timer = hoverIntentTimers.get(badge);
  if (!timer) return;
  clearTimeout(timer);
  hoverIntentTimers.delete(badge);
}

function onBadgeMouseEnter(
  event: MouseEvent,
  onHoverIntentChange?: (active: boolean) => void,
  onPointerPresenceChange?: (active: boolean) => void,
  isPointerPresent?: () => boolean
) {
  const badge = event.currentTarget;
  if (!(badge instanceof HTMLElement)) return;
  onPointerPresenceChange?.(true);
  clearHoverIntentTimer(badge);
  const timer = setTimeout(() => {
    hoverIntentTimers.delete(badge);
    if (badge.matches(':hover') || isPointerPresent?.()) {
      badge.classList.add('hover-intent');
      onHoverIntentChange?.(true);
    }
  }, HOVER_INTENT_DELAY_MS);
  hoverIntentTimers.set(badge, timer);
}

function onBadgeMouseLeave(
  event: MouseEvent,
  onHoverIntentChange?: (active: boolean) => void,
  onPointerPresenceChange?: (active: boolean) => void
) {
  const badge = event.currentTarget;
  if (!(badge instanceof HTMLElement)) return;
  clearHoverIntentTimer(badge);
  badge.classList.remove('hover-intent');
  onHoverIntentChange?.(false);
  onPointerPresenceChange?.(false);
}

export const embeddingBadgeStyles = css`
  ${unsafeCSS(
    themedScrollbarStyle(
      [
        '.affine-embedding-badge-details',
        '.affine-embedding-badge-review-panel',
        '.affine-embedding-badge-review-value',
        '.affine-embedding-badge-review-textarea',
      ],
      { size: 6, thumbMinSize: 36 }
    )
  )}

  @keyframes affine-embedding-badge-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .affine-embedding-badge {
    --affine-status-island-tone: ${unsafeCSSVarV2('text/secondary')};
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 0;
    min-height: 32px;
    padding: 4px 8px;
    border-radius: 999px;
    border: 1px solid
      color-mix(in srgb, var(--affine-status-island-tone) 42%, transparent);
    background: color-mix(
      in srgb,
      var(--affine-status-island-tone) 14%,
      ${unsafeCSSVarV2('layer/background/primary')}
    );
    color: var(--affine-status-island-tone);
    box-shadow: ${unsafeCSSVarV2('shadow/overlay')};
    font-size: 12px;
    line-height: 18px;
    box-sizing: border-box;
    user-select: none;
    cursor: pointer;
    overflow: hidden;
    transition:
      width 0.18s ease 0.16s,
      max-width 0.18s ease 0.16s,
      opacity 0.15s ease,
      transform 0.12s ease,
      box-shadow 0.12s ease,
      background-color 0.12s ease 0.16s,
      border-color 0.12s ease 0.16s,
      border-radius 0.12s ease 0.16s,
      padding 0.12s ease 0.16s;
  }

  .affine-embedding-badge-portal {
    position: fixed;
    left: 0;
    top: 0;
    z-index: calc(var(--streamlain-z-index-popover, 10000) + 20);
    pointer-events: auto;
    transform-origin: top left;
    --affine-embedding-badge-inline-max-width: calc(100vw - 16px);
  }

  .affine-embedding-badge-portal > .affine-embedding-badge {
    position: static;
    right: auto;
    bottom: auto;
  }

  .affine-embedding-badge.tone-success {
    --affine-status-island-tone: ${unsafeCSSVarV2('status/success')};
  }

  .affine-embedding-badge.tone-info {
    --affine-status-island-tone: ${unsafeCSSVarV2('button/primary')};
  }

  .affine-embedding-badge.tone-warning {
    --affine-status-island-tone: ${unsafeCSSVarV2('chip/label/orange')};
  }

  .affine-embedding-badge.tone-danger {
    --affine-status-island-tone: ${unsafeCSSVarV2('status/error')};
  }

  .affine-embedding-badge.tone-neutral {
    border-color: ${unsafeCSSVarV2('layer/insideBorder/border')};
    background: ${unsafeCSSVarV2('layer/background/primary')};
  }

  .affine-embedding-badge.loading {
    cursor: default;
  }

  .affine-embedding-badge-summary {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 22px;
    gap: 7px;
  }

  .affine-embedding-badge-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    color: var(--affine-status-island-tone);
  }

  .affine-embedding-badge-icon svg {
    width: 16px;
    height: 16px;
  }

  .affine-embedding-badge-text {
    white-space: nowrap;
    font-weight: 600;
  }

  /* Collapsed island: a circle showing only the icon until hovered, then the
     label expands in. */
  .affine-embedding-badge.collapsible {
    width: 32px;
    min-width: 32px;
    max-width: 32px;
    padding: 0 7px;
    gap: 0;
    justify-content: center;
  }

  .affine-embedding-badge.collapsible .affine-embedding-badge-summary {
    gap: 0;
  }

  /* Opened island: a neutral card. Only the icon keeps the tone, so the
     details read as text and not as a coloured alert. */
  .affine-embedding-badge.has-details.hover-intent,
  .affine-embedding-badge.has-details.expanded {
    width: 300px;
    max-width: min(
      300px,
      var(--affine-embedding-badge-inline-max-width, 300px)
    );
    align-items: stretch;
    padding: 12px;
    border-radius: 12px;
    border-color: ${unsafeCSSVarV2('layer/insideBorder/border')};
    background: ${unsafeCSSVarV2('layer/background/primary')};
    color: ${unsafeCSSVarV2('text/primary')};
    transition:
      width 0.18s ease 0.08s,
      max-width 0.18s ease 0.08s,
      opacity 0.15s ease,
      transform 0.12s ease,
      box-shadow 0.12s ease,
      background-color 0.12s ease 0.08s,
      border-color 0.12s ease 0.08s,
      border-radius 0.18s ease,
      padding 0.12s ease 0.2s;
  }

  .affine-embedding-badge.has-details.hover-intent
    .affine-embedding-badge-summary,
  .affine-embedding-badge.has-details.expanded .affine-embedding-badge-summary {
    justify-content: flex-start;
  }

  .affine-embedding-badge.has-details.review-open {
    width: auto;
    min-width: min(42rem, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
    max-height: min(760px, calc(100vh - 24px));
    align-items: stretch;
    padding: 0;
    border-radius: 14px;
    cursor: default;
    overflow: hidden;
    background: linear-gradient(
      180deg,
      color-mix(
        in srgb,
        ${unsafeCSSVarV2('layer/background/primary')} 96%,
        ${unsafeCSSVarV2('button/primary')} 4%
      ),
      ${unsafeCSSVarV2('layer/background/primary')}
    );
  }

  .affine-embedding-badge.collapsible:not(.has-details) {
    width: max-content;
  }

  .affine-embedding-badge.collapsible.hover-intent:not(.has-details) {
    max-width: min(
      260px,
      var(--affine-embedding-badge-inline-max-width, 260px)
    );
    padding: 0 10px;
  }

  .affine-embedding-badge.collapsible .affine-embedding-badge-text {
    max-width: 0;
    margin-left: 0;
    opacity: 0;
    overflow: hidden;
    transition:
      max-width 0.16s ease 0.16s,
      opacity 0.1s ease 0.16s,
      margin-left 0.16s ease 0.16s;
  }

  .affine-embedding-badge.collapsible.hover-intent .affine-embedding-badge-text,
  .affine-embedding-badge.expanded .affine-embedding-badge-text {
    max-width: 220px;
    margin-left: 6px;
    opacity: 1;
    transition:
      max-width 0.18s ease,
      opacity 0.12s ease,
      margin-left 0.18s ease;
  }

  .affine-embedding-badge.has-details.hover-intent .affine-embedding-badge-text,
  .affine-embedding-badge.has-details.expanded .affine-embedding-badge-text {
    font-size: 13px;
  }

  /* A pill: the label stays next to the icon while the island is collapsed. */
  .affine-embedding-badge.collapsible.label-visible {
    width: max-content;
    max-width: min(
      260px,
      var(--affine-embedding-badge-inline-max-width, 260px)
    );
    padding: 0 12px 0 9px;
  }

  .affine-embedding-badge.collapsible.label-visible
    .affine-embedding-badge-text {
    max-width: 220px;
    margin-left: 6px;
    opacity: 1;
    text-overflow: ellipsis;
  }

  .affine-embedding-badge.collapsible.label-visible.has-details.hover-intent {
    width: 300px;
    max-width: min(
      300px,
      var(--affine-embedding-badge-inline-max-width, 300px)
    );
    padding: 12px;
  }

  /* The folded details must not widen the pill: it hugs its label, and the
     details take the width of the opened card. */
  .affine-embedding-badge.label-visible .affine-embedding-badge-details {
    width: 0;
    min-width: 100%;
  }

  /* Indexing progress: a circular ring filling clockwise with the percentage
     centred where the status icon normally sits. Indeterminate (no count) just
     spins. Lives inside the icon slot so the badge stays a fixed circle. */
  .affine-embedding-badge-progress {
    position: relative;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .affine-embedding-badge-progress svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  .affine-embedding-badge-progress.indeterminate svg {
    animation: affine-embedding-badge-spin 0.9s linear infinite;
  }

  .affine-embedding-badge-progress-track {
    fill: none;
    stroke: ${unsafeCSSVarV2('layer/insideBorder/border')};
    stroke-width: 2.5;
  }

  .affine-embedding-badge-progress-fill {
    fill: none;
    stroke: var(--affine-status-island-tone);
    stroke-width: 2.5;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.3s ease;
  }

  .affine-embedding-badge-progress-number {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
    color: var(--affine-status-island-tone);
    font-variant-numeric: tabular-nums;
  }

  .affine-embedding-badge-details {
    width: 100%;
    box-sizing: border-box;
    color: ${unsafeCSSVarV2('text/primary')};
    cursor: default;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 0;
    margin-top: 0;
    padding-top: 0;
    border-top: 1px solid transparent;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
    visibility: hidden;
    transition:
      max-height 0.14s ease,
      margin-top 0.14s ease,
      padding-top 0.14s ease,
      opacity 0.1s ease,
      visibility 0s linear 0.14s;
  }

  .affine-embedding-badge.has-details.hover-intent
    .affine-embedding-badge-details,
  .affine-embedding-badge.expanded .affine-embedding-badge-details {
    max-height: min(60vh, 420px);
    margin-top: 10px;
    padding-top: 10px;
    border-top-color: ${unsafeCSSVarV2('layer/insideBorder/border')};
    opacity: 1;
    overflow-x: hidden;
    overflow-y: auto;
    pointer-events: auto;
    visibility: visible;
    transition:
      max-height 0.18s ease 0.3s,
      margin-top 0.18s ease 0.3s,
      padding-top 0.18s ease 0.3s,
      opacity 0.12s ease 0.32s,
      visibility 0s linear 0.3s;
  }

  .affine-embedding-badge-details-title {
    color: ${unsafeCSSVarV2('text/secondary')};
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
  }

  .affine-embedding-badge-details-body {
    color: ${unsafeCSSVarV2('text/secondary')};
    font-size: 12px;
    line-height: 18px;
    overflow-wrap: anywhere;
  }

  .affine-embedding-badge-review-panel {
    width: 100%;
    max-width: 100%;
    min-height: 0;
    box-sizing: border-box;
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    color: ${unsafeCSSVarV2('text/primary')};
    cursor: default;
  }

  .affine-embedding-badge-review-header,
  .affine-embedding-badge-review-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .affine-embedding-badge-review-header {
    padding: 0 0 12px;
    border-bottom: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
  }

  .affine-embedding-badge-review-title,
  .affine-embedding-badge-review-section-title {
    font-weight: 700;
  }

  .affine-embedding-badge-review-close,
  .affine-embedding-badge-review-button {
    border-radius: 6px;
    border: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    background: transparent;
    color: ${unsafeCSSVarV2('text/primary')};
    cursor: pointer;
    font-size: 12px;
    min-height: 28px;
    max-width: 100%;
    line-height: 16px;
    padding: 5px 10px;
    white-space: normal;
  }

  .affine-embedding-badge-review-button.primary {
    border-color: transparent;
    background: ${unsafeCSSVarV2('button/primary')};
    color: ${unsafeCSSVarV2('button/pureWhiteText')};
  }

  .affine-embedding-badge-review-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .affine-embedding-badge-review-section {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    border-radius: 10px;
    background: color-mix(
      in srgb,
      ${unsafeCSSVarV2('layer/background/secondary')} 55%,
      transparent
    );
  }

  .affine-embedding-badge-review-section-header {
    min-height: 20px;
  }

  .affine-embedding-badge-review-content {
    display: grid;
    min-width: 0;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  .affine-embedding-badge-review-section:not(.has-editor)
    .affine-embedding-badge-review-content {
    grid-template-columns: 1fr;
  }

  .affine-embedding-badge-review-generated,
  .affine-embedding-badge-review-editor {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .affine-embedding-badge-review-label {
    color: ${unsafeCSSVarV2('text/secondary')};
    font-size: 11px;
    font-weight: 600;
  }

  .affine-embedding-badge-review-value {
    min-height: 32px;
    max-height: 104px;
    overflow: auto;
    box-sizing: border-box;
    padding: 6px 8px;
    border-radius: 8px;
    border: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    background: ${unsafeCSSVarV2('layer/background/secondary')};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    text-align: left;
    color: ${unsafeCSSVarV2('text/primary')};
    font-size: 12px;
    line-height: 17px;
  }

  .affine-embedding-badge-review-muted,
  .affine-embedding-badge-review-warning {
    color: ${unsafeCSSVarV2('text/secondary')};
    font-size: 11px;
    line-height: 15px;
  }

  .affine-embedding-badge-review-warning {
    color: ${unsafeCSSVarV2('chip/label/orange')};
  }

  .affine-embedding-badge-review-input,
  .affine-embedding-badge-review-textarea {
    width: 100%;
    box-sizing: border-box;
    border-radius: 6px;
    border: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    background: ${unsafeCSSVarV2('layer/background/secondary')};
    color: ${unsafeCSSVarV2('text/primary')};
    font: inherit;
    font-size: 12px;
    line-height: 17px;
    padding: 6px 8px;
    text-align: left;
  }

  .affine-embedding-badge-review-textarea {
    min-height: 62px;
    max-height: 150px;
    resize: none;
    overflow-y: hidden;
  }

  .affine-embedding-badge-review-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .affine-embedding-badge-review-main-actions {
    justify-content: space-between;
    flex-wrap: nowrap;
  }

  .affine-embedding-badge-review-button.ghost {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding-inline: 2px 8px;
    border-color: transparent;
    color: ${unsafeCSSVarV2('text/secondary')};
  }

  .affine-embedding-badge-review-button.ghost:hover:not(:disabled) {
    background: ${unsafeCSSVarV2('layer/background/secondary')};
    color: ${unsafeCSSVarV2('text/primary')};
  }

  .affine-embedding-badge-review-button.saved {
    border-color: transparent;
    color: ${unsafeCSSVarV2('status/success')};
  }

  .affine-embedding-badge-review-regenerate {
    min-width: 0;
  }

  .affine-embedding-badge-review-regenerate-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 2px;
  }

  .affine-embedding-badge-review-hint {
    min-height: 72px;
  }

  .affine-embedding-badge-review-regenerate-actions {
    justify-content: flex-end;
  }

  .affine-embedding-badge-details-row {
    display: grid;
    grid-template-columns: minmax(64px, max-content) minmax(0, 1fr);
    align-items: baseline;
    gap: 12px;
    color: ${unsafeCSSVarV2('text/secondary')};
    font-size: 12px;
    line-height: 18px;
  }

  .affine-embedding-badge-details-row strong {
    color: ${unsafeCSSVarV2('text/primary')};
    font-weight: 500;
    text-align: right;
    overflow-wrap: anywhere;
  }

  .affine-embedding-badge-actions {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 2px;
  }

  .affine-embedding-badge-actions.multiple {
    display: grid;
    grid-template-columns: max-content max-content;
    justify-content: space-between;
    align-items: center;
  }

  .affine-embedding-badge-action {
    align-self: flex-end;
    height: 28px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: ${unsafeCSSVarV2('button/primary')};
    color: ${unsafeCSSVarV2('button/pureWhiteText')};
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
  }

  .affine-embedding-badge-action.secondary {
    background: transparent;
    color: ${unsafeCSSVarV2('text/primary')};
    border-color: ${unsafeCSSVarV2('layer/insideBorder/border')};
  }

  .affine-embedding-badge-action.secondary:hover {
    background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
  }

  @media screen and (max-width: 640px) {
    .affine-embedding-badge.has-details.review-open {
      right: 8px;
      bottom: 8px;
      width: calc(100vw - 24px);
      max-width: calc(100vw - 24px);
      max-height: calc(100vh - 24px);
      padding: 0;
    }

    .affine-embedding-badge-review-content {
      grid-template-columns: 1fr;
    }

    .affine-embedding-badge-review-panel {
      gap: 10px;
      padding: 12px;
    }

    .affine-embedding-badge-review-section {
      padding: 10px;
    }

    .affine-embedding-badge-review-textarea {
      min-height: 56px;
    }
  }
`;

const checkIcon = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3.5 8.5l3 3 6-7" />
</svg>`;

const indexIcon = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M8 2v4M8 10v4M2 8h4M10 8h4" />
  <circle cx="8" cy="8" r="2" />
</svg>`;

// Magnifier crossed out — signals "not searchable" for the disabled state.
const searchOffIcon = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="7" cy="7" r="4" />
  <path d="M10 10l3.5 3.5" />
  <path d="M2.5 13.5l11-11" />
</svg>`;

// Alert circle — signals an indexing failure (red) with a retry action.
const failedIcon = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="8" cy="8" r="6" />
  <path d="M8 5v3.5" />
  <path d="M8 11h.01" />
</svg>`;

const lockIcon = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect x="3" y="7" width="10" height="7" rx="2" />
  <path d="M5.5 7V5a2.5 2.5 0 015 0v2" />
</svg>`;

// Shield with a check — a signature that holds.
const verifiedIcon = html`<svg
  viewBox="0 0 16 16"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M8 1.8l5 1.9v3.9c0 3.1-2.1 5.5-5 6.6-2.9-1.1-5-3.5-5-6.6V3.7z" />
  <path d="M5.8 8.1l1.6 1.6 2.9-3.3" />
</svg>`;

/** Icons for islands outside the search index, drawn like the index icons. */
export const statusIslandIcons = {
  verified: verifiedIcon,
  alert: failedIcon,
} as const;

// Circular progress ring. `fraction` in [0, 1] fills clockwise from the top
// with the percentage centred; `null` spins as an indeterminate ring.
const PROGRESS_RADIUS = 7;
const PROGRESS_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RADIUS;

function renderProgressRing(fraction: number | null): TemplateResult {
  const determinate = fraction !== null;
  const dashOffset = determinate
    ? PROGRESS_CIRCUMFERENCE * (1 - fraction)
    : PROGRESS_CIRCUMFERENCE * 0.7;
  return html`
    <span
      class=${classMap({
        'affine-embedding-badge-progress': true,
        indeterminate: !determinate,
      })}
    >
      <svg viewBox="0 0 18 18">
        <circle
          class="affine-embedding-badge-progress-track"
          cx="9"
          cy="9"
          r=${PROGRESS_RADIUS}
        />
        <circle
          class="affine-embedding-badge-progress-fill"
          cx="9"
          cy="9"
          r=${PROGRESS_RADIUS}
          stroke-dasharray=${PROGRESS_CIRCUMFERENCE}
          stroke-dashoffset=${dashOffset}
          transform="rotate(-90 9 9)"
        />
      </svg>
      ${determinate
        ? html`<span class="affine-embedding-badge-progress-number"
            >${Math.round(fraction * 100)}</span
          >`
        : nothing}
    </span>
  `;
}

function statusIcon(status: EmbeddingBadgeStatus): TemplateResult {
  switch (status) {
    case 'loading':
      return renderProgressRing(null);
    case 'searchable':
      return checkIcon;
    case 'paused':
      return lockIcon;
    case 'disabled':
      return searchOffIcon;
    case 'failed':
      return failedIcon;
    case 'unavailable':
      return searchOffIcon;
    case 'not-indexed':
      return indexIcon;
  }
}

const EMBEDDING_STATUS_TONES = {
  searchable: 'success',
  paused: 'warning',
  'not-indexed': 'info',
  loading: 'info',
  disabled: 'neutral',
  failed: 'danger',
  unavailable: 'neutral',
} as const satisfies Record<EmbeddingBadgeStatus, StatusIslandTone>;

function getBadgeActions(vm: EmbeddingBadgeViewModel): EmbeddingBadgeAction[] {
  if (vm.actions?.length) return vm.actions;
  if (!vm.actionLabel || !vm.onAction) return [];
  return [{ label: vm.actionLabel, onClick: vm.onAction, kind: 'primary' }];
}

/**
 * The island every status surface shares: collapsed it is a circle (or a pill
 * when the label stays visible), opened it is a card with rows, text and
 * actions.
 */
export function renderStatusIsland(vm: StatusIslandViewModel): TemplateResult {
  const busy = vm.busy === true;
  const actions = vm.actions ?? [];
  const showReview = Boolean(vm.reviewOpen && vm.reviewPanel);
  const showExpanded = (vm.expanded && !busy) || showReview;
  const hasDetails =
    !busy &&
    (showReview ||
      Boolean(vm.rows?.length) ||
      Boolean(vm.body) ||
      actions.length > 0);
  // Every inactive state collapses. Hover reveals details; a click expands
  // them until the pointer leaves the island or the user clicks outside.
  const collapsible = !showExpanded;

  return html`
    <div
      class=${classMap({
        'affine-embedding-badge': true,
        [`tone-${vm.tone}`]: true,
        ...(vm.stateClass ? { [vm.stateClass]: true } : {}),
        loading: busy,
        'label-visible': vm.labelVisible === true,
        'review-open': showReview,
        expanded: showExpanded,
        'hover-intent': vm.hoverIntent === true,
        'has-details': hasDetails,
        collapsible,
      })}
      aria-label=${vm.label}
      @click=${vm.onClick}
      @mouseenter=${(event: MouseEvent) =>
        onBadgeMouseEnter(
          event,
          vm.onHoverIntentChange,
          vm.onPointerPresenceChange,
          vm.isPointerPresent
        )}
      @mouseleave=${(event: MouseEvent) =>
        onBadgeMouseLeave(
          event,
          vm.onHoverIntentChange,
          vm.onPointerPresenceChange
        )}
    >
      ${showReview
        ? nothing
        : html`<div class="affine-embedding-badge-summary">
            <span class="affine-embedding-badge-icon">${vm.icon}</span>
            <span class="affine-embedding-badge-text">${vm.label}</span>
          </div>`}
      ${showReview
        ? vm.reviewPanel
        : hasDetails
          ? html`
              <div
                class="affine-embedding-badge-details"
                @click=${stopPropagation}
              >
                ${vm.title
                  ? html`<div class="affine-embedding-badge-details-title">
                      ${vm.title}
                    </div>`
                  : nothing}
                ${vm.rows?.length
                  ? vm.rows.map(
                      row =>
                        html`<div class="affine-embedding-badge-details-row">
                          <span>${row.label}</span>
                          <strong>${row.value}</strong>
                        </div>`
                    )
                  : nothing}
                ${vm.body
                  ? html`<div class="affine-embedding-badge-details-body">
                      ${vm.body}
                    </div>`
                  : nothing}
                ${actions.length
                  ? html`<div
                      class=${classMap({
                        'affine-embedding-badge-actions': true,
                        multiple: actions.length > 1,
                      })}
                    >
                      ${actions.map(
                        action =>
                          html`<button
                            type="button"
                            class=${classMap({
                              'affine-embedding-badge-action': true,
                              secondary: action.kind === 'secondary',
                            })}
                            @click=${action.onClick}
                          >
                            ${action.label}
                          </button>`
                      )}
                    </div>`
                  : nothing}
              </div>
            `
          : nothing}
    </div>
  `;
}

/** The search-index badge: the index states drawn as a status island. */
export function renderEmbeddingBadge(
  vm: EmbeddingBadgeViewModel
): TemplateResult {
  const { status, progress } = vm;
  const loading = status === 'loading';
  const showDetailsTitle = Boolean(
    vm.detailTitle && vm.detailTitle !== vm.hoverLabel
  );
  const fraction =
    progress && progress.total > 0
      ? Math.min(1, Math.max(0, progress.indexed / progress.total))
      : null;

  return renderStatusIsland({
    tone: EMBEDDING_STATUS_TONES[status],
    icon: loading ? renderProgressRing(fraction) : statusIcon(status),
    label: vm.hoverLabel,
    stateClass: status,
    busy: loading,
    expanded: vm.expanded,
    title: showDetailsTitle ? vm.detailTitle : undefined,
    rows: vm.detailRows,
    body: vm.detailBody,
    actions: getBadgeActions(vm),
    onClick: vm.onBadgeClick,
    hoverIntent: vm.hoverIntent,
    isPointerPresent: vm.isPointerPresent,
    onHoverIntentChange: vm.onHoverIntentChange,
    onPointerPresenceChange: vm.onPointerPresenceChange,
    reviewOpen: vm.reviewOpen,
    reviewPanel: vm.reviewPanel,
  });
}

/**
 * Builds the badge view-model from raw status signals. Single source of truth
 * for which state a badge is in and which labels it shows, so every block type
 * renders identical states. Returns null while the index status is still
 * unknown (nothing to show yet).
 *
 * The workspace embedding policy (`enabled`) takes precedence: when it is
 * explicitly off the badge enters the `disabled` state regardless of index
 * status. An unknown policy (`null`) is treated as enabled so the badge is
 * never hidden by a slow/failed policy fetch.
 */
export function buildEmbeddingBadgeViewModel(params: {
  indexed: boolean | null;
  loading: boolean;
  expanded: boolean;
  enabled: boolean | null;
  unavailable?: boolean;
  imageAnalysis?: {
    state: 'complete' | 'missing';
    availability: 'available' | 'paused_no_credits' | 'unavailable';
  } | null;
  reindexCreditsBlocked?: boolean;
  /** Last indexing attempt failed; only surfaced while content is not indexed. */
  failed?: boolean;
  progress?: { indexed: number; total: number } | null;
  service: Pick<IEmbeddingStatusService, 'getLabel'> | null | undefined;
  onBadgeClick: (event: MouseEvent) => void;
  onAction?: (event: MouseEvent) => void;
  onDetails?: (event: MouseEvent) => void;
  onCheckAgain?: (event: MouseEvent) => void;
}): EmbeddingBadgeViewModel | null {
  const {
    indexed,
    loading,
    expanded,
    enabled,
    failed,
    progress,
    service,
    unavailable,
  } = params;
  const label = (key: Parameters<typeof getEmbeddingStatusLabel>[1]) =>
    getEmbeddingStatusLabel(service, key);
  const detailRows = (statusLabel: string): EmbeddingBadgeDetailRow[] => [
    { label: label('searchIndex'), value: statusLabel },
  ];
  const detailsAction = params.onDetails
    ? [
        {
          label: label('reviewDetails'),
          onClick: params.onDetails,
          kind: 'primary' as const,
        },
      ]
    : [];

  if (enabled === false) {
    return {
      status: 'disabled',
      expanded,
      hoverLabel: label('notSearchable'),
      detailTitle: label('embeddingDisabledTitle'),
      detailRows: detailRows(label('indexStatusDisabled')),
      detailBody: label('embeddingDisabledHint'),
      onBadgeClick: params.onBadgeClick,
    };
  }

  if (loading) {
    return {
      status: 'loading',
      expanded: false,
      progress,
      hoverLabel: progress
        ? getEmbeddingStatusLabel(service, 'indexingDocuments', progress)
        : label('indexingInProgress'),
      detailTitle: label('indexingInProgress'),
      detailRows: detailRows(label('indexStatusIndexing')),
      onBadgeClick: params.onBadgeClick,
    };
  }

  if (unavailable) {
    return {
      status: 'unavailable',
      expanded,
      hoverLabel: label('indexStatusUnavailable'),
      detailTitle: label('indexStatusUnavailable'),
      detailBody: label('indexStatusUnavailableHint'),
      detailRows: detailRows(label('indexStatusUnavailable')),
      actions: params.onCheckAgain
        ? [
            {
              label: label('checkAgain'),
              onClick: params.onCheckAgain,
              kind: 'primary',
            },
          ]
        : undefined,
      onBadgeClick: params.onBadgeClick,
    };
  }

  // Unknown state stays hidden while the first check is pending. A completed
  // check that failed must remain visible so the user can retry it.
  if (indexed === null && !failed) return null;

  if (indexed === false && params.reindexCreditsBlocked) {
    return {
      status: 'paused',
      expanded,
      hoverLabel: label('indexingPaused'),
      detailTitle: label('indexingPaused'),
      detailBody: label('indexingPausedHint'),
      detailRows: detailRows(label('indexStatusNotIndexed')),
      onBadgeClick: params.onBadgeClick,
      actions: [
        ...(params.onAction
          ? [
              {
                label: label('reindex'),
                onClick: params.onAction,
                kind: 'secondary' as const,
              },
            ]
          : []),
        ...detailsAction,
      ],
    };
  }

  if (indexed && params.imageAnalysis?.state === 'missing') {
    const creditsPaused =
      params.imageAnalysis.availability === 'paused_no_credits';
    return {
      status: creditsPaused ? 'paused' : 'failed',
      expanded,
      hoverLabel: label(
        creditsPaused ? 'imageAnalysisPaused' : 'imageAnalysisFailed'
      ),
      detailTitle: label(
        creditsPaused ? 'imageAnalysisPaused' : 'imageAnalysisFailed'
      ),
      detailBody: label(
        creditsPaused ? 'imageAnalysisPausedHint' : 'imageAnalysisFailedHint'
      ),
      detailRows: detailRows(label('indexStatusIndexed')),
      onBadgeClick: params.onBadgeClick,
      actions: [
        ...(params.onAction
          ? [
              {
                label: label('reindex'),
                onClick: params.onAction,
                kind: 'secondary' as const,
              },
            ]
          : []),
        ...detailsAction,
      ],
    };
  }

  if (indexed) {
    return {
      status: 'searchable',
      expanded,
      hoverLabel: label('indexedForSearch'),
      detailTitle: label('indexedForSearch'),
      detailBody: params.reindexCreditsBlocked
        ? `${label('imageAnalysisPausedHint')} ${label('existingIndexPreserved')}`
        : failed
          ? `${label('imageAnalysisFailedHint')} ${label('existingIndexPreserved')}`
          : undefined,
      detailRows: detailRows(label('indexStatusIndexed')),
      actionLabel: label('reindex'),
      onBadgeClick: params.onBadgeClick,
      onAction: params.onAction,
      actions: [
        ...(params.onAction
          ? [
              {
                label: label('reindex'),
                onClick: params.onAction,
                kind: 'secondary' as const,
              },
            ]
          : []),
        ...detailsAction,
      ],
    };
  }

  // A failed attempt is only meaningful while the content is still not indexed
  // (already-indexed content stays searchable even if a re-index later fails).
  if (failed) {
    return {
      status: 'failed',
      expanded,
      hoverLabel: label('indexingFailed'),
      detailTitle: label('indexingFailed'),
      detailBody: label('indexingFailedHint'),
      detailRows: detailRows(label('indexStatusFailed')),
      actionLabel: label('reindex'),
      onBadgeClick: params.onBadgeClick,
      onAction: params.onAction,
      actions: [
        ...(params.onAction
          ? [
              {
                label: label('reindex'),
                onClick: params.onAction,
                kind: 'secondary' as const,
              },
            ]
          : []),
        ...detailsAction,
      ],
    };
  }

  return {
    status: 'not-indexed',
    expanded,
    hoverLabel: label('notSearchable'),
    detailTitle: label('notSearchable'),
    detailBody: label('notIndexedHint'),
    detailRows: detailRows(label('indexStatusNotIndexed')),
    actionLabel: label('clickToIndexForSearch'),
    onBadgeClick: params.onBadgeClick,
    onAction: params.onAction,
    actions: [
      ...(params.onAction
        ? [
            {
              label: label('clickToIndexForSearch'),
              onClick: params.onAction,
              kind: 'secondary' as const,
            },
          ]
        : []),
      ...detailsAction,
    ],
  };
}
